import { useEffect, useRef, useState } from 'react'
import { useInView, useReducedMotion } from 'motion/react'
import { ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { ScrollReveal, TextReveal, useMediaQuery } from '@/components/motion'
import { OfficeAppIcon } from '@/components/landing/OfficeAppIcon'
import { storyMilestones, type StoryMilestone } from '@/data/about'
import { HALFTONE_STYLE, Highlight } from './Highlight'

const TILE_BG = ['#ff00f2', 'var(--color-landing-accent)', 'var(--color-landing-secondary)', '#0a0a0a'] as const
const EASE = 'ease-[cubic-bezier(0.22,1,0.36,1)]'

/* ---- Tile art: product fragments instead of the reference's photos ---- */

function PagesArt() {
  // Fanned stack of documents: the busywork the story starts from.
  return (
    <div className="absolute right-[8%] top-[10%] h-[62%] w-[34%]">
      {[-9, -3, 4].map((deg, i) => (
        <div
          key={deg}
          className="absolute inset-0 bg-white p-[9%] shadow-[0_18px_30px_-18px_rgb(0_0_0_/_0.45)]"
          style={{ transform: `rotate(${deg}deg) translateX(${(i - 1) * 14}%)` }}
        >
          <div className="h-[6%] w-[60%] bg-[var(--color-landing-gray-800)]" />
          {[88, 94, 72, 90, 60, 84, 78].map((w, j) => (
            <div key={j} className="mt-[7%] h-[3%] bg-[var(--color-landing-gray-300)]" style={{ width: `${w}%` }} />
          ))}
        </div>
      ))}
    </div>
  )
}

function RolesArt() {
  // The three jobs from the story: proposals, clauses, formulas.
  const rows = [
    { app: 'word' as const, label: 'Proposal formatting' },
    { app: 'word' as const, label: 'Clause copy & paste' },
    { app: 'excel' as const, label: 'Formula checks' },
  ]
  return (
    <ul className="absolute right-[6%] top-[9%] grid w-[50%] grid-cols-[minmax(0,1fr)] gap-[0.4rem] sm:right-[7%] sm:top-[11%] sm:w-[46%] sm:gap-[0.6rem]">
      {rows.map((row) => (
        <li key={row.label} className="flex min-w-0 items-center gap-2 bg-white px-2 py-1.5 shadow-[0_14px_24px_-16px_rgb(0_0_0_/_0.5)] sm:gap-3 sm:px-3 sm:py-2.5">
          <OfficeAppIcon app={row.app} className="h-5 w-5 shrink-0 sm:h-7 sm:w-7" />
          <span className="min-w-0 truncate text-[11px] font-semibold text-[var(--color-landing-gray-800)] sm:text-sm">{row.label}</span>
          <span className="ml-auto hidden h-1.5 w-8 shrink-0 bg-[var(--color-landing-gray-200)] sm:block" />
        </li>
      ))}
    </ul>
  )
}

function PaneArt() {
  // A Templatean task pane: document tools that understand the work.
  return (
    <div className="absolute right-[7%] top-[10%] w-[40%] bg-white shadow-[0_18px_30px_-18px_rgb(0_0_0_/_0.5)]">
      <div className="flex items-center gap-2 border-b border-[var(--color-landing-gray-200)] px-3 py-2">
        <span className="text-xs font-bold tracking-[-0.01em] text-[var(--color-landing-gray-900)]">
          templ<span className="text-[var(--color-landing-accent)]">a</span>tean
        </span>
        <span className="ml-auto h-1.5 w-6 bg-[var(--color-landing-gray-200)]" />
      </div>
      <div className="grid gap-2 p-3">
        {['Draft', 'Polish', 'Brand check'].map((label, i) => (
          <div key={label} className="flex items-center gap-2">
            <span className={cn('h-3 w-3 shrink-0', i === 2 ? 'bg-[#ff00f2]' : 'bg-[var(--color-landing-accent)]')} />
            <span className="text-[11px] font-semibold text-[var(--color-landing-gray-700)] sm:text-xs">{label}</span>
            <span className="ml-auto h-1.5 w-[30%] bg-[var(--color-landing-gray-200)]" />
          </div>
        ))}
        <span className="mt-1 block bg-[var(--color-landing-primary)] py-1.5 text-center text-[10px] font-bold uppercase tracking-wide text-white">
          Insert
        </span>
      </div>
    </div>
  )
}

function ShotArt() {
  // The real product: a crop of the workflow builder.
  return (
    <div className="absolute right-[6%] top-[9%] h-[60%] w-[48%] overflow-hidden border border-white/20 bg-white">
      <img
        src="/images/screenshots/workflow-builder.png"
        alt=""
        loading="lazy"
        decoding="async"
        className="h-full w-full object-cover"
        style={{ objectPosition: '44% 30%' }}
      />
    </div>
  )
}

const ART = [PagesArt, RolesArt, PaneArt, ShotArt]

function MilestoneTile({
  milestone,
  index,
  bright,
  tileRef,
}: {
  milestone: StoryMilestone
  index: number
  /** Full opacity; false dims the tile to 0.5 (outside the centre band). */
  bright: boolean
  tileRef: (el: HTMLDivElement | null) => void
}) {
  const Art = ART[index % ART.length] ?? PagesArt

  return (
    <li>
      <div
        ref={tileRef}
        aria-hidden
        className={cn(
          'relative aspect-[3/2] overflow-hidden [container-type:inline-size] transition-opacity duration-700 dark:ring-1 dark:ring-inset dark:ring-white/10',
          EASE
        )}
        style={{ background: TILE_BG[index % TILE_BG.length], opacity: bright ? 1 : 0.5 }}
      >
        <div className="absolute inset-0 opacity-50" style={HALFTONE_STYLE} />
        <Art />
        <span className="absolute bottom-[5%] left-[5%] font-[family-name:var(--font-landing-display)] text-[clamp(2.5rem,11cqw,7rem)] uppercase leading-[1.1] tracking-[-0.03em] text-white">
          {milestone.marker}
        </span>
      </div>
      {/* Accessible copy; visible under each tile below lg, where the
          crossfading slot is hidden. */}
      <div className="mt-5 lg:sr-only">
        <h3 className="font-[family-name:var(--font-landing-subdisplay)] text-xl font-bold uppercase tracking-[-0.01em] text-[var(--color-landing-gray-900)] dark:text-white">
          {milestone.title}
        </h3>
        <p className="mt-3 landing-text-body text-[var(--color-landing-gray-700)] dark:text-[var(--color-landing-gray-300)]">
          {milestone.text}
        </p>
      </div>
    </li>
  )
}

/**
 * Journey timeline (.4fr / 1fr): sticky heading column whose milestone slot
 * crossfades to the tile in the viewport's centre band; 3:2 tiles with big
 * markers; a 5px centre line (mix-blend-mode: difference) with a pink fill
 * whose head sticks to the viewport centre. Below lg everything is static
 * flow with the copy under each tile.
 */
export function JourneyTimeline() {
  const desktop = useMediaQuery('(min-width: 1024px)')
  const reduce = useReducedMotion()
  const listRef = useRef<HTMLOListElement>(null)
  const tileRefs = useRef<(HTMLDivElement | null)[]>([])
  const near = useInView(listRef, { margin: '50% 0px 50% 0px' })
  // Slot content: the last tile whose top has passed the viewport centre.
  const [active, setActive] = useState(0)
  // Tile currently crossing the centre band (middle 10% of the viewport).
  const [inBand, setInBand] = useState(-1)

  // Measured from the tiles' rects on scroll (rAF-throttled, only while the
  // timeline is near the viewport) so jumps via anchors or keyboard stay right.
  useEffect(() => {
    if (!desktop || !near) return
    let frame = 0
    const measure = () => {
      frame = 0
      const mid = window.innerHeight / 2
      const half = window.innerHeight * 0.05
      let nextActive = 0
      let nextBand = -1
      tileRefs.current.forEach((el, i) => {
        if (!el) return
        const r = el.getBoundingClientRect()
        if (r.top <= mid) nextActive = i
        if (r.top < mid + half && r.bottom > mid - half) nextBand = i
      })
      setActive(nextActive)
      setInBand(nextBand)
    }
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure)
    }
    measure()
    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
    }
  }, [desktop, near])

  const dimmable = desktop && !reduce

  return (
    <section aria-labelledby="story-title" className="landing-section">
      <div className="landing-container grid items-start gap-14 lg:grid-cols-[minmax(0,0.4fr)_minmax(0,1fr)] lg:gap-20">
        <div className="lg:sticky lg:top-28 lg:flex lg:h-[calc(100svh-9rem)] lg:flex-col">
          <TextReveal as="h2" className="landing-text-display-xl text-[var(--color-landing-gray-900)] dark:text-white">
            <span id="story-title">
              Our story, <Highlight color="pink">so far.</Highlight>
            </span>
          </TextReveal>
          <ScrollReveal direction="none" delay={0.15}>
            <p className="mt-8 max-w-[34ch] border-l border-[var(--color-landing-gray-300)] pl-5 landing-text-body-lg dark:border-[var(--color-landing-gray-700)] text-[color-mix(in_srgb,var(--color-landing-gray-900)_66%,transparent)] dark:text-[color-mix(in_srgb,white_62%,transparent)]">
              One frustration, one mission: help organizations create better documents in less time.
            </p>
            <Link
              to="/contact"
              className="group mt-10 inline-flex items-center gap-3 font-[family-name:var(--font-landing-display)] text-lg uppercase text-[var(--color-landing-gray-900)] transition-colors duration-200 hover:text-[var(--color-landing-accent)] dark:text-white"
            >
              Get in touch
              <ArrowRight aria-hidden className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-0.5" />
            </Link>
          </ScrollReveal>

          {/* Crossfading milestone slot (visual; the tiles carry the copy) */}
          <div aria-hidden className="mt-auto hidden pt-10 lg:grid">
            {storyMilestones.map((m, i) => (
              <div
                key={m.title}
                className={cn('[grid-area:1/1] transition-[opacity,transform] duration-500', EASE)}
                style={{
                  opacity: i === active ? 1 : 0,
                  transform: i === active ? 'translateY(0)' : 'translateY(10px)',
                }}
              >
                <p className="font-[family-name:var(--font-landing-display)] text-[1.6rem] uppercase leading-[1.4] tracking-[-0.02em] text-[var(--color-landing-gray-900)] dark:text-white">
                  {m.title}
                </p>
                <span className="mt-4 block h-px w-16 bg-[var(--color-landing-gray-300)] dark:bg-[var(--color-landing-gray-700)]" />
                <p className="mt-4 max-w-[42ch] landing-text-body text-[color-mix(in_srgb,var(--color-landing-gray-900)_66%,transparent)] dark:text-[color-mix(in_srgb,white_62%,transparent)]">
                  {m.text}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="relative">
          {/* Centre line, inverted against whatever it crosses */}
          <div aria-hidden className="pointer-events-none absolute inset-y-0 left-1/2 z-10 hidden w-[5px] -translate-x-1/2 bg-white mix-blend-difference lg:block" />
          {/* Pink fill whose head sticks at the viewport centre; overflow:clip
              keeps sticky working while trimming the fill above the track. */}
          <div aria-hidden className="pointer-events-none absolute inset-y-0 left-1/2 z-20 hidden w-4 -translate-x-1/2 [overflow:clip] lg:block">
            <div className="sticky top-[50vh] h-0">
              <div className="absolute bottom-0 left-1/2 h-[200vh] w-[5px] -translate-x-1/2 bg-[#ff00f2]" />
              <div className="absolute left-1/2 top-0 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 bg-[#ff00f2]" />
            </div>
          </div>

          <ol ref={listRef} className="grid gap-16 lg:gap-24">
            {storyMilestones.map((milestone, index) => (
              <MilestoneTile
                key={milestone.title}
                milestone={milestone}
                index={index}
                bright={!dimmable || index === inBand}
                tileRef={(el) => {
                  tileRefs.current[index] = el
                }}
              />
            ))}
          </ol>
        </div>
      </div>
    </section>
  )
}
